import React, {
  RefObject,
  useMemo,
  useState,
  useCallback,
  SyntheticEvent,
  ComponentType,
  useEffect,
} from 'react';
import clsx from 'clsx';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import AutoCompletedInput from 'core/components/AutoComplete';
import Spinner from 'pages/Dashboard/components/Spinner';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  FilterLookupTranslator,
} from 'core/Filters/types';
import useSearchLookupOptions from 'pages/Dashboard/pages/Billing/Charges/hooks';
import Dropdown from 'core/components/Lookup/Generic/Dropdown';
import {
  PagePropertiesDTO,
} from 'dtos/pagePropertiesDTO';
import compact from 'lodash/compact';

export type LookupOptionType = Record<string, any>;
export type LookupOptionProps = {
  option: LookupOptionType;
  props: Record<string, any>;
  lookupTranslator?: FilterLookupTranslator;
};

function DefaultLookupTranslatorOption({
  option,
  lookupTranslator,
  props,
}: LookupOptionProps) {
  const {
    idField,
    displayNameField,
    displaySubtitle1Field,
    displaySubtitle2Field,
  } = lookupTranslator ?? {};

  const subtitleFields = lookupTranslator
    ? compact([
      option?.[displaySubtitle1Field ?? ''],
      option?.[displaySubtitle2Field ?? ''],
    ])
    : [];

  return (
    <div
      {...props}
      key={option?.[idField ?? '']}
      className={clsx(
        props?.className,
        'flex flex-row !items-center !w-full gap-3',
      )}
    >
      <div className="flex flex-col !items-start !justify-start !w-full !text-left gap-2">
        <span className="font-semibold">
          {option?.[displayNameField ?? ''] ?? '--'}
        </span>
        {subtitleFields.length > 0 && (
          <span className="flex items-center gap-2 populate-vertical-list-text">
            {compact(subtitleFields).map((item: string) => (
              <span key={item} className="populate-list-item">{item}</span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}

type PaginatedData<T> = {
  data?: T[];
  pageProperties?: PagePropertiesDTO;
};

type Parts = 'root' | 'inputRoot' | 'dropdown' | 'popup';

type Props<T> = {
  className?: string;
  id: string;
  value?: LookupOptionType | null;
  label?: string;
  placeholder: string;
  readonly?: boolean;
  onChange: (value: T) => void;
  inputRef?: RefObject<HTMLInputElement>;
  resetOnEmpty?: boolean;
  required?: boolean;
  error?: boolean;
  filterOptions?: (options: LookupOptionType[]) => LookupOptionType[];
  labelClasses?: string;
  selectOnFocus?: boolean;
  multiple?: boolean;
  renderTags?: () => null;
  renderOptionComponent?: ComponentType<LookupOptionProps>;
  clearOnBlur?: boolean;
  showLoading?: boolean;
  autoFocus?: boolean;
  isOptionEqualToValue?: (option: LookupOptionType, value: LookupOptionType | string) => boolean;
  optionLookupUrl?: string | null;
  dropdownClassName?: string;
  lookupTranslator?: FilterLookupTranslator;
  dropdownId?: string;
  debounceDelay?: number;
  minSearchSize?: number;
  defaultPageSize?: number;
  classes?: Partial<Record<Parts, string>>;
  blurOnSelect?: boolean;
};

export function getNoOptionsText(
  options: readonly LookupOptionType[],
  inputValue: string,
  isLoading: boolean = false,
  initialMessage: string = 'Start typing to see the list',
  noResultMessage: string = 'No result found',
) {
  const message = options?.length === 0 && isEmptyString(inputValue)
    ? initialMessage
    : noResultMessage;

  return isLoading ? 'Searching...' : message;
}

export default function Lookup<T extends LookupOptionType>({
  className,
  id,
  onChange,
  placeholder,
  value: _value,
  readonly,
  label = '',
  inputRef,
  required = false,
  error = false,
  autoFocus = true,
  filterOptions = (x) => x,
  labelClasses = 'text-gray-500',
  selectOnFocus = false,
  isOptionEqualToValue,
  multiple = false,
  renderTags,
  clearOnBlur = false,
  optionLookupUrl,
  lookupTranslator,
  dropdownClassName,
  dropdownId,
  debounceDelay = 700,
  showLoading = true,
  renderOptionComponent: RenderOptionComponent,
  minSearchSize = 1,
  defaultPageSize = 10,
  classes: _classes,
  blurOnSelect = false,
}: Props<T>) {
  const { idField, displayNameField } = lookupTranslator ?? {};
  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const value = multiple ? _value : { [displayNameField ?? '']: _value ?? '' } as LookupOptionType;

  const classes = clsx('flex flex-col flex-auto', {
    '!space-y-0': isEmptyString(label),
    'space-y-1': notEmptyString(label),
  });
  const containerClasses = clsx('flex flex-wrap flex-row w-full space-x-4 mb-3', className, _classes?.root);
  const searchEnabled = inputValue.trim().length >= minSearchSize;

  const {
    data,
    isFetching,
    isLoading: loading,
  } = useSearchLookupOptions(
    optionLookupUrl ?? '',
    inputValue,
    searchEnabled && open,
    0,
    (page + 1) * defaultPageSize,
  );

  const {
    data: _lookupOptions = [],
    pageProperties = undefined,
  } = ((data as PaginatedData<T>)?.pageProperties ? (data as PaginatedData<T>) : { data }) ?? {};

  useEffect(() => {
    if (!loading && _lookupOptions.length > 0) {
      setOpen(true);
    }
  }, [loading, _lookupOptions]);

  const fetchNextPage = useCallback(() => {
    if (!isNil(pageProperties)) {
      setPage(page + 1);
    }
  }, [page, setPage, pageProperties]);

  const isLoading = useMemo(() => (
    showLoading ? (isFetching || loading) : false
  ), [isFetching, loading]);
  const handleInputChange = useCallback(debounce((
    event: SyntheticEvent,
    newInputValue: string,
  ) => {
    if (open) {
      setInputValue(newInputValue);
    }
  }, debounceDelay), [open]);

  const handleChange = (value: LookupOptionType | LookupOptionType[]) => {
    setOpen(false);
    onChange(value as T);
  };

  const noOptionsText = useMemo(
    () => getNoOptionsText(_lookupOptions, inputValue, isLoading),
    [_lookupOptions, inputValue, isLoading],
  );

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div className={containerClasses}>
      <Wrapper
        id="lookup-select"
        className={classes}
        required={required}
        error={error}
        showErrorText={false}
        labelClasses={labelClasses}
        label={label}
      >
        <AutoCompletedInput<LookupOptionType>
          value={multiple ? value : undefined}
          isOptionEqualToValue={
            isNil(isOptionEqualToValue)
              ? (option, value) => (
                typeof value === 'string'
                  ? option?.[displayNameField ?? ''] === value
                  : option?.[displayNameField ?? ''] === value?.[displayNameField ?? '']
              )
              : isOptionEqualToValue
          }
          id={id}
          blurOnSelect={blurOnSelect}
          disabled={readonly}
          options={_lookupOptions ?? []}
          onOpen={() => setOpen(true)}
          onClose={handleClose}
          open={open}
          multiple={multiple}
          slotProps={{
            listbox: {
              component: Dropdown,
              pageProperties: isNil(pageProperties) ? null : pageProperties,
              fetchNextPage,
              isLoading,
              id: dropdownId,
              count: _lookupOptions?.length,
            },
          } as Object}
          getLabel={(option) => option?.[displayNameField ?? ''] ?? '--'}
          renderTags={renderTags}
          loading={isLoading}
          renderOption={(props: Record<string, any>, option: LookupOptionType) => (
            !isNil(RenderOptionComponent)
              ? (
                <RenderOptionComponent
                  key={option?.[idField ?? '']}
                  props={props}
                  option={option}
                  lookupTranslator={lookupTranslator}
                />
              )
              : (
                <DefaultLookupTranslatorOption
                  key={option?.[idField ?? '']}
                  props={props}
                  option={option}
                  lookupTranslator={lookupTranslator}
                />
              )
          )}
          inputRef={inputRef}
          defaultValue={notEmptyString((value)?.[displayNameField ?? '']) ? value : undefined}
          className="w-full"
          onChange={handleChange}
          filterOptions={filterOptions}
          placeholder={placeholder}
          onInputChange={handleInputChange}
          classes={{ inputRoot: clsx('!py-[7px] !pr-8', _classes?.inputRoot) }}
          clearOnBlur={clearOnBlur}
          selectOnFocus={selectOnFocus}
          noOptionsText={noOptionsText}
          dropdownClassName={clsx('!overflow-hidden p-2', dropdownClassName)}
          popupIcon={isLoading ? <Spinner /> : undefined}
          InputProps={{
            autoFocus,
          }}
        />
      </Wrapper>
    </div>
  );
}
